import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { IdCard, Image as ImageIcon, ShieldCheck } from "lucide-react";
import { adminApi } from "../utils/adminApi";
import "./TrackRepair.css";

const TrackRepair = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const didFetch = useRef(false);

  const query = new URLSearchParams(location.search);
  const urlTrackingId = query.get("trackingId") || "";
  const urlPhone = query.get("phone") || "";

  const [trackingId, setTrackingId] = useState(urlTrackingId);
  const [phone, setPhone] = useState(urlPhone);

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  /* ================= FETCH BOOKING ================= */
  const fetchBooking = useCallback(async () => {
    if (!trackingId || !phone) {
      setError("Booking ID and mobile number required");
      return;
    }

    try {
      setLoading(true);
      setError("");

      // ✅ FIXED API
      const res = await adminApi.post("/api/bookings/track", {
        trackingId: trackingId.trim(),
        phone: phone.trim(),
      });

      setBooking(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Tracking failed");
    } finally {
      setLoading(false);
    }
  }, [trackingId, phone]);

  useEffect(() => {
    if (urlTrackingId && urlPhone && !didFetch.current) {
      didFetch.current = true;
      fetchBooking();
    }
  }, [urlTrackingId, urlPhone, fetchBooking]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchBooking();
  };

  /* ================= OTP VERIFY ================= */
  const handleVerifyOtp = async () => {
    if (!booking) return;

    const cleanOtp = otp.replace(/\D/g, "").slice(0, 6);

    if (!cleanOtp) {
      setOtpError("Enter OTP");
      return;
    }

    try {
      setOtpLoading(true);
      setOtpError("");

      // ✅ FIXED OTP ROUTE
      await adminApi.post(`/api/bookings/${booking._id}/verify-otp`, {
        otp: cleanOtp,
      });

      navigate("/thank-you");
    } catch {
      setOtpError("Invalid OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Track Repair Status | Customer Supports 24x7 Bangalore</title>
        <meta
          name="description"
          content="Customer Supports 24x7 offers fast mobile and appliance repair services in Bangalore. Trusted doorstep repair for mobiles, washing machines, refrigerators, ACs and more."
        />
        <meta
          name="keywords"
          content="mobile repair Bangalore, appliance repair Bangalore, washing machine repair Bangalore, refrigerator repair Bangalore, AC repair Bangalore, doorstep repair service, Customer Supports 24x7"
        />
        <meta property="og:title" content="Track Repair Status | Customer Supports 24x7 Bangalore" />
        <meta
          property="og:description"
          content="Customer Supports 24x7 offers fast mobile and appliance repair services in Bangalore. Trusted doorstep repair for mobiles, washing machines, refrigerators, ACs and more."
        />
        <meta property="og:image" content="https://customersupports24x7.in/customerSupport.png" />
        <meta property="og:url" content="https://customersupports24x7.in/track-repair" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Track Repair Status | Customer Supports 24x7 Bangalore" />
        <meta
          name="twitter:description"
          content="Customer Supports 24x7 offers fast mobile and appliance repair services in Bangalore. Trusted doorstep repair for mobiles, washing machines, refrigerators, ACs and more."
        />
        <meta name="twitter:image" content="https://customersupports24x7.in/customerSupport.png" />
        <link rel="canonical" href="https://customersupports24x7.in/track-repair" />
      </Helmet>
    <div className="track-page">
      <div className="track-card">

        <h1>Track Your Booking</h1>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Booking ID"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
          />

          <input
            type="tel"
            placeholder="Mobile Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />

          <button type="submit">
            {loading ? "Tracking..." : "Track"}
          </button>
        </form>

        {error && <p>{error}</p>}

        {booking && (
          <div>
            <h3>Order Details</h3>
            <p>ID: {booking.trackingId}</p>
            <p>Status: {booking.status}</p>
            <p>Service: {booking.service}</p>
            <p>Phone: {booking.phone}</p>

            <h3>Technician</h3>
            <p>{booking.technicianName || "Not Assigned"}</p>

            {/* OTP */}
            <input
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />

            <button onClick={handleVerifyOtp}>
              {otpLoading ? "Verifying..." : "Verify OTP"}
            </button>

            {otpError && <p>{otpError}</p>}
          </div>
        )}

      </div>
    </div>
    </>
  );
};

export default TrackRepair;

